import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, User, Settings, LogOut, PanelLeft } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import { spacing } from '../../config/spacing';
import { typography } from '../../config/typography';
import { Button } from '../UI/button';
import { CommandPaletteTrigger } from './CommandPalette';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../UI/dropdown-menu';

const Header = ({ onMenuClick, user, onLogout, sidebarCollapsed, onToggleSidebar, onOpenCommandPalette }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLocale();
  const { title: pageTitle, description: pageDescription } = usePageHeader();
  const headerTitleStyle = typography.textStyles.headerTitle;

  const getPageTitle = () => {
    if (pageTitle != null && pageTitle !== '') return pageTitle;
    const path = location.pathname;
    const keyMap = {
      '/dashboard': 'dashboardTitle',
      '/companies': 'companiesTitle',
      '/watchlist': 'watchlistTitle',
      '/my-companies': 'myCompaniesTitle',
      '/my-tenant-company': 'myTenantCompanyTitle',
      '/news': 'newsTitle',
      '/settings': 'settingsTitle',
    };
    return t(keyMap[path] || 'dashboardTitle');
  };

  const getPageDescription = () => {
    if (pageDescription != null && pageDescription !== '') return pageDescription;
    const path = location.pathname;
    const keyMap = {
      '/dashboard': 'dashboardDescription',
      '/companies': 'companiesDescription',
      '/watchlist': 'watchlistDescription',
      '/my-companies': 'myCompaniesDescription',
      '/my-tenant-company': 'myTenantCompanyDescription',
      '/news': 'newsDescription',
      '/settings': 'settingsDescription',
    };
    return t(keyMap[path] || 'welcomeBackChainSight');
  };

  return (
    <header
      className="sticky top-0 z-40 border-b border-border-light bg-background-secondary shadow-elevation-1"
      style={{ height: spacing.layout.header }}
    >
      <div className="flex h-full items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-4">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onMenuClick}
            className="lg:hidden shrink-0"
            aria-label={t('sidebarOpenMenu')}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onToggleSidebar}
            className="hidden shrink-0 lg:flex"
            aria-label={
              sidebarCollapsed ? t('sidebarExpandSidebar') : t('sidebarCollapseSidebar')
            }
          >
            <PanelLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1 lg:flex-initial">
            <h1
              className="truncate font-semibold tracking-tight text-text-primary"
              style={{
                fontSize: headerTitleStyle.fontSize,
                lineHeight: headerTitleStyle.lineHeight,
                letterSpacing: headerTitleStyle.letterSpacing,
              }}
            >
              {getPageTitle()}
            </h1>
            <p className="hidden truncate text-sm text-text-secondary sm:block">
              {getPageDescription()}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <div className="hidden w-48 sm:block lg:w-64">
            <CommandPaletteTrigger onClick={onOpenCommandPalette} />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-circle"
                aria-label={t('headerUserMenu')}
              >
                <User className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="border-b border-border-light px-4 py-3">
                <p className="text-sm font-medium text-text-primary">
                  {user?.firstname} {user?.lastname}
                </p>
                <p className="text-xs text-text-secondary">{user?.email}</p>
              </div>
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                {t('headerSettings')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onLogout}
                className="focus:bg-danger-50 focus:text-danger-600"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t('headerSignOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;
